import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string; // DEPRECATED: Meta keywords ignored by search engines since 2009. Kept for legacy support.
  image?: string;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({ 
  title, 
  description, 
  keywords,
  image = 'https://jeanbilbao.com/assets/images/og-image.jpg',
  noindex = false
}) => {
  const { lang = 'en' } = useParams<{ lang: string }>();
  const location = useLocation();
  const { t } = useTranslation();

  // Base site URL
  const baseUrl = 'https://jeanbilbao.com';
  
  // Get current path without language prefix for alternates
  const pathWithoutLang = location.pathname.replace(`/${lang}`, '') || '';
  
  // Construct canonical URL
  const canonicalUrl = `${baseUrl}${location.pathname}`;
  
  // Construct alternate URLs
  const enUrl = `${baseUrl}/en${pathWithoutLang}`;
  const frUrl = `${baseUrl}/fr${pathWithoutLang}`;
  
  // Default meta content
  const metaTitle = title || t('hero.title');
  const metaDescription = description || t('hero.tagline');
  const fullTitle = `${metaTitle} | Jean Bilbao`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Alternates */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="fr" href={frUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'fr' ? 'en_US' : 'fr_FR'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Robots Meta */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};

export default SEO;
